import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import xUpdateRequestRma from "@stoneage/App/Handlers/Pages/RequestRma/UpdateRequestRma";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const billToState = getBillToState(state, state.context.session.billToId);
    let billTo;
    if (billToState && billToState.value) {
        billTo = billToState.value;
    }
    return {
        session: state.context.session,
        billTo,
    };
};

const mapDispatchToProps = {
    xUpdateRequestRma,
};

export interface RequestRmaAccountInformationStyles {
    titleText?: TypographyProps;
    billingInformationTitleText?: TypographyProps;
    gridContainerStyles?: GridContainerProps;
    ContactInformationGridItem?: GridItemProps;
}

export const requestRmaAccountInformationStyles: RequestRmaAccountInformationStyles = {
    billingInformationTitleText: {
        as: "h2",
        variant: "h5",
        css: css`
            margin-bottom: 10px;
            font-size: 20px;
            line-height: 24px;
            ${xScreen("lte", "md")} {
                margin-top: 16px;
            }
        `,
    },
    gridContainerStyles: {
        css: css``,
    },
    ContactInformationGridItem: {
        width: 12,
        css: css``,
    },
};

/**
 * @deprecated Use requestRmaAccountInformationStyles instead.
 */
export const statusStyles = requestRmaAccountInformationStyles;
const styles = requestRmaAccountInformationStyles;

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const RequestRmaAccountInformation: FC<Props> = ({ xUpdateRequestRma, session, billTo }: Props) => {
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");

    const [phoneError, setPhoneError] = useState("");
    const [emailError, setEmailError] = useState("");

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage as string);
        return !errorMessage;
    };

    const validatePhone = (phone: string) => {
        let errorMessage;
        if (phone.trim().length > 0 && !/^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/g.test(phone)) {
            errorMessage = siteMessage("AddressInfo_Phone_Validation") as string;
        } else {
            errorMessage = "";
        }
        setPhoneError(errorMessage);
        return !errorMessage;
    };
    const handlePhoneChange = (phone: string) => {
        if (validatePhone(phone)) {
            xUpdateRequestRma({ properties: { phone } } as any);
        } else {
            xUpdateRequestRma({ properties: { phone: "" } } as any);
        }
    };
    const handleEmailChange = (email: string) => {
        if (validateEmail(email)) {
            xUpdateRequestRma({ properties: { email } } as any);
        } else {
            xUpdateRequestRma({ properties: { email: "" } } as any);
        }
    };
    useEffect(() => {
        xUpdateRequestRma({
            properties: {
                name: `${session.firstName} ${session.lastName}`,
                phone: billTo?.phone,
                email: session.email,
            } as any,
        });
    }, [billTo?.phone]);
    return (
        <>
            <Typography {...styles.billingInformationTitleText}>{translate("Contact Information")}</Typography>
            <GridContainer {...styles.gridContainerStyles}>
                <GridItem {...styles.ContactInformationGridItem}>
                    <TextField
                        onChange={e => xUpdateRequestRma({ properties: { name: e.target.value } } as any)}
                        type="name"
                        label={translate("Name")}
                        data-test-selector="RmaContactInfo_name"
                        required
                        translate="no"
                        defaultValue={`${session.firstName} ${session.lastName}`}
                    />
                </GridItem>
                <GridItem {...styles.ContactInformationGridItem}>
                    <TextField
                        onChange={e => handlePhoneChange(e.currentTarget.value)}
                        type="phone"
                        label={translate("phone")}
                        data-test-selector="RmaContactInfo_phone"
                        error={phoneError}
                        required
                        defaultValue={billTo?.phone}
                    />
                </GridItem>
                <GridItem {...styles.ContactInformationGridItem}>
                    <TextField
                        onChange={e => handleEmailChange(e.target.value)}
                        type="email"
                        label={translate("Email")}
                        data-test-selector="RmaContactInfo_email"
                        error={emailError}
                        required
                        defaultValue={session.email}
                    />
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaAccountInformation),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
